import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RoomStageInfo, SolvedPuzzleMessage } from '../../common/types';
import { useMemo, useState } from 'react';
import { RoomStageDetailedProgress } from '../stages/RoomStageDetailedProgress';

export type RenderedSolvedPuzzleMessageProps = {
  message: SolvedPuzzleMessage;
  stages: RoomStageInfo[];
};

export default function RenderedSolvedPuzzleMessage(props: RenderedSolvedPuzzleMessageProps) {
  const { message } = props;

  const puzzle = useMemo(() => {
    for (const stage of props.stages) {
      for (const puzzle of stage.puzzles) {
        if (puzzle.name === message.name) {
          return puzzle;
        }
      }
    }
    return null;
  }, [message, props.stages]);

  const textContent = (
    <div className="space-y-2 bg-gray-800 rounded-lg shadow-md py-2 px-4 border border-teal-500">
      <h3 className="text-base font-semibold">
        {message.name} - <b>Solved</b>
      </h3>
      <ReactMarkdown
        // Note: We replace all newlines with two spaces and a newline to ensure that the markdown
        // renderer correctly renders line breaks.
        children={message.content.replaceAll('\n', '  \n')}
        className="prose prose-sm md:prose-lg lg:prose-xl"
        components={{
          ul: ({ node, ...props }) => <ul className="list-disc list-inside pl-2" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal list-inside pl-2" {...props} />,
          li: ({ node, ...props }) => <li className="mb-2 space-y-4" {...props} />,
          div: ({ node, ...props }) => <div {...props} className="space-y-4" />,
          code: ({ node, ...props }) => (
            <code
              {...props}
              className="bg-gray-800 text-gray-300 p-1 rounded-lg break-words whitespace-pre-wrap"
            />
          ),
        }}
        remarkPlugins={[remarkGfm]}
      />
      <div className="pt-4">
        <RoomStageDetailedProgress
          stages={props.stages}
          solvedPuzzle={puzzle?.puzzleUuid ?? undefined}
        />
      </div>
    </div>
  );

  return textContent;
}
